/**
 * App Crashes — Round 17 (Sep 14 2026).
 *
 * Uncaught JS errors reported by the mobile apps (POST /api/client-errors):
 * what broke, on which screen, in which build, and who hit it. Build 1.0.13
 * died on every open of the Notifications screen and the iOS crash log
 * carried no reason — this page is the visibility that was missing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Bug,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Trash2,
  Smartphone,
  Users,
  AlertTriangle,
  Clock,
  X,
} from 'lucide-react';

const PAGE_SIZE = 25;

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const PlatformPill = ({ platform }) => {
  const label = platform === 'ios' ? 'iOS' : platform === 'android' ? 'Android' : platform || '?';
  const cls = platform === 'ios'
    ? 'bg-gray-900 text-white'
    : platform === 'android'
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
};

const StatCard = ({ label, value, icon: Icon, tone }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-2">{value ?? 0}</p>
      </div>
      <div className={`p-3 rounded-lg ${tone}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const AppCrashesPage = () => {
  const { api } = useAdmin();
  const [data, setData] = useState({ errors: [], total: 0, summary: {}, groups: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [platform, setPlatform] = useState('');
  const [fatalOnly, setFatalOnly] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (platform) params.set('platform', platform);
      if (fatalOnly) params.set('fatal_only', 'true');
      if (fingerprint) params.set('fingerprint', fingerprint);
      const r = await api.get(`/api/admin/client-errors?${params.toString()}`);
      setData({
        errors: r.data?.errors || [],
        total: r.data?.total || 0,
        summary: r.data?.summary || {},
        groups: r.data?.groups || [],
      });
    } catch (e) {
      toast.error('Failed to load crash reports');
    } finally {
      setLoading(false);
    }
  }, [api, page, platform, fatalOnly, fingerprint]);

  useEffect(() => { load(); }, [load]);

  const clear = async (fp) => {
    const what = fp ? 'this crash group' : 'ALL crash reports';
    if (!window.confirm(`Delete ${what}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/client-errors${fp ? `?fingerprint=${encodeURIComponent(fp)}` : ''}`);
      toast.success('Cleared');
      if (fp && fingerprint === fp) setFingerprint('');
      setPage(1);
      load();
    } catch (e) {
      toast.error('Failed to clear');
    }
  };

  const pickGroup = (fp) => {
    setFingerprint(fp === fingerprint ? '' : fp);
    setPage(1);
  };

  const { errors, total, summary, groups } = data;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-emerald-600" />
            App Crashes
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Errors the mobile apps reported on their own — screen, build, and who hit it. Kept for 30 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => clear('')}
            disabled={total === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-rose-200 rounded-lg hover:bg-rose-50 text-sm font-medium text-rose-600 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Clear all
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Last 24 hours" value={summary.last_24h} icon={Clock} tone="bg-amber-50 text-amber-600" />
        <StatCard label="Last 7 days" value={summary.last_7d} icon={AlertTriangle} tone="bg-rose-50 text-rose-600" />
        <StatCard label="Fatal (7 days)" value={summary.fatal_7d} icon={Bug} tone="bg-gray-100 text-gray-700" />
        <StatCard label="People affected (7 days)" value={summary.users_7d} icon={Users} tone="bg-emerald-50 text-emerald-600" />
      </div>

      {/* Top crash groups */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Bug className="w-5 h-5 text-rose-500" />
            Top crashes — last 7 days
          </h2>
          <span className="text-xs text-gray-400">Click a row to filter the list below</span>
        </div>
        {groups.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm">No crash groups in the last 7 days</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Hits</th>
                  <th className="text-left px-4 py-2">Error</th>
                  <th className="text-left px-4 py-2">Screen</th>
                  <th className="text-left px-4 py-2">Build</th>
                  <th className="text-left px-4 py-2">People</th>
                  <th className="text-left px-4 py-2">Last seen</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {groups.map((g) => (
                  <tr
                    key={g.fingerprint}
                    onClick={() => pickGroup(g.fingerprint)}
                    className={`cursor-pointer hover:bg-gray-50 ${fingerprint === g.fingerprint ? 'bg-emerald-50' : ''}`}
                  >
                    <td className="px-4 py-3 font-semibold text-gray-900">{g.count}</td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="flex items-center gap-2">
                        {g.fatal && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-bold">FATAL</span>}
                        <span className="font-medium text-gray-900 truncate">{g.name}: {g.message}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{g.screen || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center gap-2">
                        <PlatformPill platform={g.platform} />
                        <span>{g.app_version}{g.build ? ` (${g.build})` : ''}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{g.users}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{when(g.last_seen)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); clear(g.fingerprint); }}
                        className="p-1.5 rounded hover:bg-rose-50 text-gray-400 hover:text-rose-600"
                        title="Clear this group"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <h2 className="font-semibold text-gray-900 flex-1">All reports</h2>
          <select
            value={platform}
            onChange={(e) => { setPlatform(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700"
          >
            <option value="">All platforms</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
            <option value="web">Web</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={fatalOnly} onChange={(e) => { setFatalOnly(e.target.checked); setPage(1); }} />
            Fatal only
          </label>
          {fingerprint && (
            <button
              onClick={() => pickGroup(fingerprint)}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium"
            >
              group {fingerprint}
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
          </div>
        ) : errors.length === 0 ? (
          <div className="text-center py-12">
            <Bug className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-gray-500">No crash reports</p>
            <p className="text-sm text-gray-400 mt-1">The apps report uncaught errors here on their own</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {errors.map((err) => {
                const isOpen = openId === err.id;
                return (
                  <div key={err.id} className="p-4 hover:bg-gray-50">
                    <button className="w-full text-left" onClick={() => setOpenId(isOpen ? null : err.id)}>
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${err.fatal ? 'text-rose-500' : 'text-amber-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span>{when(err.created_at)}</span>
                            <PlatformPill platform={err.platform} />
                            <span>{err.app_version}{err.build ? ` (${err.build})` : ''}{err.os_version ? ` · OS ${err.os_version}` : ''}</span>
                            {err.screen && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{err.screen}</span>}
                            <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${err.fatal ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                              {err.fatal ? 'FATAL' : (err.kind || 'error').replace('_', ' ')}
                            </span>
                            {err.user_name && <span className="text-gray-700">{err.user_name}</span>}
                          </div>
                          <p className="text-sm text-gray-900 font-medium mt-1 break-words">{err.name}: {err.message}</p>
                        </div>
                        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="mt-3 ml-8 space-y-3">
                        {err.stack && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Stack</p>
                            <pre className="text-xs bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">{err.stack}</pre>
                          </div>
                        )}
                        {err.component_stack && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Component stack</p>
                            <pre className="text-xs bg-gray-50 text-gray-700 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">{err.component_stack}</pre>
                          </div>
                        )}
                        <p className="text-xs text-gray-400">
                          group {err.fingerprint}{err.user_id ? ` · user ${err.user_id}` : ' · not signed in'}{err.js_version ? ` · JS ${err.js_version}` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Total: {total} reports</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">Page {page} of {lastPage}</span>
                <button
                  onClick={() => setPage(Math.min(lastPage, page + 1))}
                  disabled={page >= lastPage}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AppCrashesPage;
